import { Carousel, PageLayout } from 'components/common';
import dashboardBackground from 'components/common/Icons/dashboardBackground.svg';
import { DashboardSearchbar, ViewRecommendations } from 'components/core';
import { mapViews } from 'components/core/ViewToggle/utils';
import { useContext, useMemo, useState } from 'react';
import { ListContext, ViewContext } from 'utils/context';
import { usePermissions } from 'utils/hooks';
import useSearchOrders from 'utils/hooks/useSearchOrders/useSearchOrders';
import useSearchShipments from 'utils/hooks/useSearchShipments/useSearchShipments';

import { Box, Flex, Typography } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { useUser } from '@project44/ui-utils';

import { formatCount } from '../ViewToggleOption/utils';
import {
  AnalyticsCards,
  checkPermission,
  CreateOrderCard,
  CreateShipmentCard,
  mapDarkCard,
  mapOrderCard,
  mapShipmentCard,
  permissionsToCheck,
  skeletons,
} from './constants';

const styles = {
  root: {
    bgColor: '$palette-grey-50',
    flex: 1,
    position: 'relative',
    width: '100%',
  },
  content: {
    flex: 1,
    width: '100%',
    height: '100%',
    minWidth: '400px',
    overflow: 'auto',
    padding: '$medium 0 $x-large',
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 0,
    '&::before': {
      backgroundImage: `url(${dashboardBackground})`,
      content: '',
      height: 408,
      position: 'absolute',
      top: 0,
      width: '100%',
      zIndex: -1,
    },
  },
  header: {
    padding: '$medium $large',
    '@media (max-width: 719px)': {
      alignItems: 'flex-start',
      flexDirection: 'column-reverse',
    },
  },
  cards: {
    '@media (max-width: 719px)': {
      justifyContent: 'center',
    },
  },
};

function ViewDashBoard() {
  const { t } = useTranslation();
  const {
    flags: { featureExc2284 },
  } = usePermissions();
  const shipments = useSearchShipments();
  const orders = useSearchOrders();
  const { data: user } = useUser();
  const permissions = user?.authorities;
  const { totalCountOrders, totalCountShipments } = useContext(ListContext);
  const { savedOrders, savedShipments } = useContext(ViewContext);
  const [shownCache, setShownCache] = useState<{ [key: string]: string }>({});

  const analyticsPermissions = {
    truckloadPermission: checkPermission(
      permissions,
      permissionsToCheck.truckloadPermission,
    ),
    oceanPermission: checkPermission(
      permissions,
      permissionsToCheck.oceanPermission,
    ),
    portIntelPermission: checkPermission(
      permissions,
      permissionsToCheck.portIntelPermission,
    ),
    rootCausePermission: checkPermission(
      permissions,
      permissionsToCheck.rootCausePermission,
    ),
    oceanSustainability: checkPermission(
      permissions,
      permissionsToCheck.oceanSustainability,
    ),
  };

  const allCards = useMemo(
    () =>
      [
        {
          count: formatCount(totalCountShipments) || 'N/A',
          label: t('core.viewAllEntity', { entity: t('core.shipmentsLower') }),
          link: '/results?filters=(listView:all-shipments)',
          accentColor: '$palette-blue-500',
          viewType: 'shipments',
        },
        {
          count: formatCount(totalCountOrders) || 'N/A',
          label: t('core.viewAllEntity', { entity: t('core.ordersLower') }),
          link: '/results?filters=(listView:all-orders)',
          accentColor: '$palette-pink-500',
          viewType: 'orders',
        },
      ].map(mapDarkCard),
    [totalCountOrders, totalCountShipments, t],
  );

  const shipmentCards = useMemo(
    () =>
      mapViews(savedShipments, {
        namespace: t('core.myShipments'),
        accentColor: '$palette-blue-500',
        listType: 'shipment',
      }).map((entity) =>
        mapShipmentCard(
          entity,
          async (key: string) => {
            const view = savedShipments.data.views.find(({ id }) => key === id);
            const count = await shipments.getCount(view?.filterCriteria);
            const formattedCount = formatCount(count);
            setShownCache((prev) => ({
              ...prev,
              [key]: formattedCount,
            }));
          },
          (key: string) => shownCache[key],
        ),
      ),
    [shipments, savedShipments, shownCache, t],
  );

  const orderCards = useMemo(
    () =>
      mapViews(savedOrders, {
        namespace: t('core.myOrders'),
        accentColor: '$palette-pink-500',
        listType: 'order',
      }).map((entity) =>
        mapOrderCard(
          entity,
          async (key: string) => {
            const view = savedOrders.data.views.find(({ id }) => key === id);
            const count = await orders.getCount(view?.filterCriteria);
            const formattedCount = formatCount(count);
            setShownCache((prev) => ({
              ...prev,
              [key]: formattedCount,
            }));
          },
          (key: string) => shownCache[key],
        ),
      ),
    [orders, savedOrders, shownCache, t],
  );

  return (
    <PageLayout
      disableBanner={false}
      content={
        <Box css={styles.root}>
          <Flex orientation="vertical" css={styles.content}>
            <Flex
              align="center"
              justify="between"
              gap="large"
              css={styles.header}
            >
              <Typography
                as="p"
                variant="heading"
                css={{ color: '$text-contrast', whiteSpace: 'nowrap' }}
              >
                {t('core.welcomeBack', { userName: user?.firstName })}
              </Typography>
              <DashboardSearchbar />
            </Flex>

            <Flex
              gap="medium"
              wrap
              css={{
                padding: featureExc2284
                  ? '$small $large $small'
                  : '$small $large $x-large',
                ...styles.cards,
              }}
            >
              {allCards}
            </Flex>

            {featureExc2284 && (
              <Box css={{ padding: '$medium $large $large' }}>
                <ViewRecommendations />
              </Box>
            )}

            <Carousel
              title={t('core.entityViews', { entity: t('core.shipment') })}
              titleColor={featureExc2284 ? '' : 'white'}
              defaultCard={CreateShipmentCard}
              isLoading={savedShipments.isLoading}
            >
              {savedShipments.isLoading ? skeletons : shipmentCards}
            </Carousel>

            <Carousel
              title={t('core.entityViews', { entity: t('core.order') })}
              defaultCard={CreateOrderCard}
              isLoading={savedOrders.isLoading}
            >
              {savedOrders.isLoading ? skeletons : orderCards}
            </Carousel>

            <Carousel title={t('core.analyticsDashboards')}>
              {AnalyticsCards(analyticsPermissions)}
            </Carousel>
          </Flex>
        </Box>
      }
    />
  );
}

export default ViewDashBoard;
